import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import WifiIcon from "@mui/icons-material/Wifi";
import CategoryIcon from "@mui/icons-material/Category";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Link as RouterLink, Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BusyButton } from "./shared";
import { PLATFORM_CONSOLE_BASE, PLATFORM_CONSOLE_PATH } from "./platformConsoleRoutes";
import { usePlatformConsoleAcl } from "./PlatformConsoleAclContext";
import { clearActiveWorkspaceBrowserStorage } from "../../../utils/workspaceLocalStorage";

const DRAWER_WIDTH = 272;

function navSectionKeyFromPath(navPath) {
  const s = String(navPath || "").replace(`${PLATFORM_CONSOLE_BASE}/`, "").split("/")[0];
  return s || "workspaces";
}

const NAV_ITEMS = [
  { path: PLATFORM_CONSOLE_PATH.workspaces, match: `${PLATFORM_CONSOLE_BASE}/workspaces`, Icon: WorkOutlineIcon, labelKey: "platformPage.tabWorkspaces" },
  {
    path: PLATFORM_CONSOLE_PATH.opportunities,
    match: `${PLATFORM_CONSOLE_BASE}/opportunities`,
    Icon: TrendingUpIcon,
    labelKey: "platformPage.tabOpportunities",
  },
  { path: PLATFORM_CONSOLE_PATH.insights, match: `${PLATFORM_CONSOLE_BASE}/insights`, Icon: AnalyticsIcon, labelKey: "platformPage.navInsights" },
  { path: PLATFORM_CONSOLE_PATH.presence, match: `${PLATFORM_CONSOLE_BASE}/presence`, Icon: WifiIcon, labelKey: "platformPage.navPresence" },
  { path: PLATFORM_CONSOLE_PATH.segments, match: `${PLATFORM_CONSOLE_BASE}/segments`, Icon: CategoryIcon, labelKey: "platformPage.tabSegments" },
  {
    path: PLATFORM_CONSOLE_PATH.governance,
    match: `${PLATFORM_CONSOLE_BASE}/governance`,
    Icon: AdminPanelSettingsIcon,
    labelKey: "platformPage.tabGovernance",
  },
  { path: PLATFORM_CONSOLE_PATH.catalog, match: `${PLATFORM_CONSOLE_BASE}/catalog`, Icon: Inventory2Icon, labelKey: "platformPage.tabCatalogOps" },
];

function pathMatchesNav(pathname, navMatch) {
  if (pathname === navMatch) return true;
  if (pathname.startsWith(`${navMatch}/`)) return true;
  return false;
}

const ConsoleShell = () => {
  const { t } = useTranslation("app");
  const theme = useTheme();
  const location = useLocation();

  useEffect(() => {
    clearActiveWorkspaceBrowserStorage({ preserveTenantDatabaseKeysForWorkspaceMaster: true });
  }, []);
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [globalError, setGlobalError] = useState("");
  const [actionToast, setActionToast] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const { loading: aclLoading, can } = usePlatformConsoleAcl();

  const refreshRef = useRef(() => {});

  const stopRefreshing = useCallback(() => setRefreshing(false), []);

  const registerRefresh = useCallback((fn) => {
    refreshRef.current = typeof fn === "function" ? fn : () => {};
  }, []);

  const runRefresh = useCallback(() => {
    setRefreshing(true);
    try {
      refreshRef.current();
    } catch {
      stopRefreshing();
    }
  }, [stopRefreshing]);

  const showActionToast = useCallback((msg) => {
    if (msg) setActionToast(msg);
    stopRefreshing();
  }, [stopRefreshing]);

  const outletContext = useMemo(
    () => ({
      registerRefresh,
      stopRefreshing,
      setGlobalError,
      showActionToast,
    }),
    [registerRefresh, stopRefreshing]
  );

  const onDetail =
    location.pathname.startsWith(`${PLATFORM_CONSOLE_BASE}/workspaces/`) &&
    location.pathname !== PLATFORM_CONSOLE_PATH.workspaces;

  const onWorkspaceCreate = location.pathname === PLATFORM_CONSOLE_PATH.workspaceCreate;

  const visibleNavItems = useMemo(() => {
    if (aclLoading) return NAV_ITEMS;
    return NAV_ITEMS.filter((item) => can(navSectionKeyFromPath(item.path), "view"));
  }, [aclLoading, can]);

  const drawer = (
    <Box sx={{ pt: 1, pb: 2 }} component="nav" aria-label={t("platformPage.consoleNavAriaLabel")}>
      <Typography variant="overline" sx={{ px: 2.5, color: "text.secondary", letterSpacing: 0.6 }}>
        {t("platformPage.consoleBadge")}
      </Typography>
      <List dense sx={{ mt: 0.5 }}>
        {visibleNavItems.map(({ path, match, Icon, labelKey }) => {
          const selected =
            pathMatchesNav(location.pathname, match) || (labelKey === "platformPage.tabWorkspaces" && onDetail);
          return (
            <ListItemButton
              key={path}
              component={RouterLink}
              to={path}
              selected={selected}
              onClick={() => setMobileOpen(false)}
              sx={{
                mx: 1,
                borderRadius: 1,
                "&.Mui-selected": {
                  bgcolor: (th) => alpha(th.palette.primary.main, 0.12),
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Icon fontSize="small" color={selected ? "primary" : "inherit"} />
              </ListItemIcon>
              <ListItemText primary={t(labelKey)} primaryTypographyProps={{ fontWeight: selected ? 600 : 500, fontSize: "0.9375rem" }} />
            </ListItemButton>
          );
        })}
      </List>
    </Box>
  );

  return (
    <Box sx={{ pt: 1, pb: 4 }}>
      <a href="#platform-console-main" style={{ position: "absolute", left: -9999 }}>
        {t("platformPage.skipToContent")}
      </a>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} sx={{ mb: 2, px: { xs: 0, md: 0 } }}>
        <Box sx={{ minWidth: 0, display: "flex", alignItems: "flex-start", gap: 1 }}>
          {!isMdUp ? (
            <IconButton aria-label={t("platformPage.openMenu")} onClick={() => setMobileOpen(true)} edge="start" size="small">
              <MenuIcon />
            </IconButton>
          ) : null}
          <Box>
            <Chip label={t("platformPage.consoleBadge")} size="small" variant="outlined" sx={{ mb: 1 }} />
            <Typography variant="h4" component="h1">
              {t("platformPage.title")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 720 }}>
              {t("platformPage.consolePurposeOneLiner")}
            </Typography>
          </Box>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
          {onWorkspaceCreate ? null : (
            <Tooltip title={t("platformPage.refreshSectionTooltip")}>
              <span>
                <BusyButton
                  startIcon={<RefreshIcon />}
                  variant="outlined"
                  size="small"
                  onClick={runRefresh}
                  loading={refreshing}
                  loadingText={t("billingPage.refresh")}
                >
                  {t("billingPage.refresh")}
                </BusyButton>
              </span>
            </Tooltip>
          )}
        </Stack>
      </Stack>

      {globalError ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setGlobalError("")}>
          {globalError}
        </Alert>
      ) : null}

      <Snackbar
        open={Boolean(actionToast)}
        autoHideDuration={6000}
        onClose={() => setActionToast("")}
        message={actionToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />

      <Box sx={{ display: "flex", alignItems: "stretch", gap: 0 }}>
        {isMdUp ? (
          <Paper
            variant="outlined"
            sx={{
              width: DRAWER_WIDTH,
              flexShrink: 0,
              borderRadius: 2,
              mr: 2,
              alignSelf: "flex-start",
              position: "sticky",
              top: 16,
              maxHeight: "calc(100vh - 32px)",
              overflow: "auto",
            }}
          >
            {drawer}
          </Paper>
        ) : (
          <Drawer anchor="left" open={mobileOpen} onClose={() => setMobileOpen(false)} PaperProps={{ sx: { width: DRAWER_WIDTH } }}>
            {drawer}
            <Divider />
            <Box sx={{ p: 2 }}>
              <Button fullWidth variant="outlined" onClick={() => setMobileOpen(false)}>
                {t("platformPage.closeMenu")}
              </Button>
            </Box>
          </Drawer>
        )}

        <Box id="platform-console-main" tabIndex={-1} sx={{ flex: 1, minWidth: 0 }} component="main">
          <Outlet context={outletContext} />
        </Box>
      </Box>
    </Box>
  );
};

export default ConsoleShell;
